import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { Alert, AlertDescription, AlertTitle, Badge, Button, PageHeader, PageState, SearchField, SegmentedControl } from "@fox1ck-org/ui";
import { BindingMode, BindingStatus, TrackingDomain } from "@gen/tracker/v1/tracking_domain_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { ConnectDomainDrawer } from "@/components/domains/connect-domain-drawer";
import { DomainDrawer, providerLabel } from "@/components/domains/domain-drawer";
import { ListView } from "@/components/list-view";
import { PlanLimit } from "@/components/plan/plan-limit";
import { useCrumbTail } from "@/lib/crumb";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { useT } from "@/lib/i18n";
import { DASH, formatInt } from "@/lib/format";
import { STATE_TONE, bindingErrorText, domainState, isProviderUnavailable, stateLabelKey } from "@/lib/domain-binding";
import { listSort } from "@/lib/services/catalog";
import { useGetTrackingDomain, useListTrackingDomains, useSyncTrackingDomain } from "@/lib/services/tracking-domain-service";
import { formatStamp } from "@/lib/utils";

/** The columns the server sorts the domains by. */
const SORT_FIELDS: Record<string, string> = { fqdn: "fqdn", state: "binding_status", campaigns: "campaigns", checked: "binding_checked_at" };

type Usage = "all" | "busy" | "free" | "problems";
const USAGES: Usage[] = ["all", "busy", "free", "problems"];

/**
 * «Домены» (доски DomainList, DomainDrawer): the addresses ad links live on —
 * each with the state of its binding, how many campaigns use it, who holds it
 * and when it was last checked; the chips Все · Заняты · Свободны · Проблемы
 * are the server's counts. A domain opens in the drawer over the list, at
 * ?domain=<id>. Every domain is searched, sorted and paged by the server.
 */
export default function DomainsListPage() {
  const t = useT();
  const params = useTableParams({ defaultSort: [{ id: "fqdn", desc: false }] });
  const usage = (USAGES.includes(params.getFilter("usage") as Usage) ? params.getFilter("usage") : "all") as Usage;
  const search = useDebounced(params.search.trim());
  const list = useListTrackingDomains({
    search,
    usage: usage === "all" ? "" : usage,
    sort: listSort(params.sorting, SORT_FIELDS),
    limit: params.pageSize,
    offset: params.pageIndex * params.pageSize,
  });
  const sync = useSyncTrackingDomain();
  const openId = params.getFilter("domain");
  const focused = useGetTrackingDomain(openId || undefined);
  const [connecting, setConnecting] = useState(false);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  // The drawer shows the freshest copy: the list row once it is here, the single read before.
  const open = items.find((d) => d.id === openId) ?? focused.data;
  useCrumbTail(open?.fqdn);
  const counts = list.data?.counts;

  const check = (d: TrackingDomain) =>
    sync.mutate(d.id, {
      onSuccess: (r) => (r.bindingStatus === BindingStatus.BOUND ? toast.success(t("domains.check.bound", { fqdn: r.fqdn })) : toast.warning(t("domains.check.waitingPlain"))),
      onError: () => toast.error(t("domains.check.failed")),
    });

  const columns = useMemo<ColumnDef<TrackingDomain>[]>(
    () => [
      {
        id: "fqdn",
        header: t("domains.col.domain"),
        size: 380,
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{row.original.fqdn}</span>
            {row.original.isDefault ? <Badge variant="outline">{t("domains.badge.default")}</Badge> : null}
          </span>
        ),
      },
      {
        id: "state",
        header: t("domains.col.state"),
        size: 210,
        cell: ({ row }) => {
          const s = domainState(row.original);
          return (
            <Badge variant={STATE_TONE[s]} dot pulse={s === "waiting"} title={bindingErrorText(row.original) || undefined}>
              {t(stateLabelKey(s))}
            </Badge>
          );
        },
      },
      {
        id: "campaigns",
        header: t("ops.dom.col.campaigns"),
        size: 90,
        meta: { align: "right" },
        cell: ({ row }) => (row.original.campaigns > 0 ? <span className="font-mono tabular-nums">{formatInt(row.original.campaigns)}</span> : <span className="text-muted-foreground">{DASH}</span>),
      },
      {
        id: "where",
        header: t("ops.dom.col.where"),
        enableSorting: false,
        size: 220,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {providerLabel(t, row.original)} · {t(row.original.bindingMode === BindingMode.OVERLAY ? "ops.dom.mode.overlay" : "ops.dom.mode.direct")}
          </span>
        ),
      },
      {
        id: "checked",
        header: t("ops.dom.col.checked"),
        size: 120,
        meta: { align: "right" },
        cell: ({ row }) => <span className="font-mono text-xs tabular-nums text-muted-foreground">{row.original.bindingCheckedAt ? formatStamp(row.original.bindingCheckedAt, params.tz) : DASH}</span>,
      },
      {
        id: "sync",
        header: "",
        enableSorting: false,
        size: 44,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("ops.dom.checkNow")}
            title={t("ops.dom.checkNow")}
            disabled={sync.isPending}
            onClick={(e) => {
              e.stopPropagation();
              check(row.original);
            }}
          >
            <RefreshCw className="size-3.5" />
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, params.tz, sync.isPending],
  );

  const providerMissing = isProviderUnavailable(list.error);
  // A link straight to a domain the user cannot see closes itself, not the page.
  useEffect(() => {
    if (openId && focused.isError) params.setFilter("domain", null);
  }, [openId, focused.isError, params]);

  const usageLabel: Record<Usage, string> = { all: t("ops.dom.usage.all"), busy: t("ops.dom.usage.busy"), free: t("ops.dom.usage.free"), problems: t("ops.dom.usage.problems") };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 px-5 pb-4 pt-4">
      <PageHeader
        title={t("nav.domains")}
        subtitle={t("ops.dom.subtitle")}
        actions={
          <WriteOnly lock={t("ops.dom.connect")}>
            <Button variant="primary" onClick={() => setConnecting(true)} disabled={providerMissing}>
              <Plus className="size-4" /> {t("ops.dom.connect")}
            </Button>
          </WriteOnly>
        }
      />
      <PlanLimit meter="tracker.domains" />
      {providerMissing ? (
        <Alert variant="warn">
          <div className="min-w-0">
            <AlertTitle>{t("domains.unavailable.title")}</AlertTitle>
            <AlertDescription>{t("domains.unavailable.body")}</AlertDescription>
          </div>
        </Alert>
      ) : list.isError ? (
        <PageState mode="error" title={t("domains.loadFailed")} error={list.error} retry={() => void list.refetch()} retryLabel={t("lib.common.retry")} />
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2">
            <SegmentedControl
              size="sm"
              aria-label={t("ops.dom.usage")}
              value={usage}
              onValueChange={(v) => params.setFilter("usage", v === "all" ? null : v)}
              options={USAGES.map((u) => ({ value: u, label: usageLabel[u], count: counts ? counts[u] : undefined }))}
            />
            <SearchField value={params.search} onChange={params.setSearch} label={t("domains.search.label")} placeholder={t("ops.dom.search")} className="ml-auto w-70" />
          </div>
          <ListView
            columns={columns}
            data={items}
            getRowId={(d) => d.id}
            loading={list.isLoading}
            manualSorting
            density="compact"
            sorting={params.sorting}
            onSortingChange={params.onSortingChange}
            pagination={{
              pageIndex: params.pageIndex,
              pageSize: params.pageSize,
              totalCount: list.data?.total ?? 0,
              onPageIndexChange: params.setPageIndex,
              onPageSizeChange: params.setPageSize,
              pageSizeOptions: params.pageSizeOptions,
            }}
            onRowClick={(d) => params.setFilter("domain", d.id)}
            rowClassName={(d) => (d.id === openId ? "bg-surface-2" : undefined)}
            emptyState={
              params.search.trim() || usage !== "all" ? (
                <PageState mode="empty" title={t("ops.dom.none")} />
              ) : (
                <PageState
                  mode="empty"
                  title={t("domains.empty.title")}
                  description={t("domains.empty.body")}
                  action={
                    <WriteOnly>
                      <Button variant="primary" onClick={() => setConnecting(true)}>
                        <Plus className="size-4" /> {t("domains.empty.action")}
                      </Button>
                    </WriteOnly>
                  }
                />
              )
            }
          />
        </>
      )}
      <DomainDrawer
        domain={openId ? open : undefined}
        tz={params.tz}
        onOpenChange={(o) => {
          if (!o) params.setFilter("domain", null);
        }}
      />
      <ConnectDomainDrawer
        open={connecting}
        onOpenChange={setConnecting}
        onCreated={(d) => {
          setConnecting(false);
          params.setFilter("domain", d.id);
        }}
      />
    </div>
  );
}
